import React from 'react';
import { PDFViewer } from '../PDFViewer';
import { DOCUMENTS_PANEL } from './constants';

/**
 * Modal that displays a PDF document for viewing only (no download).
 * urlOverride: when set (e.g. free sample topics URL), use instead of doc.fileUrl/previewUrl.
 */
export function DocumentPdfModal({ isOpen, doc, useFullDocument = false, urlOverride, onClose }) {
  if (!isOpen || !doc) return null;

  const title = doc.displayName || doc.name;
  const url = urlOverride ?? (useFullDocument ? (doc.fileUrl || doc.previewUrl) : (doc.previewUrl || doc.fileUrl));
  const isPreview = !urlOverride && !useFullDocument && doc.hasPassword && doc.previewUrl;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`View document: ${title}`}
      style={backdropStyle}
      onClick={onClose}
    >
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <header style={headerStyle}>
          <div style={headerContentStyle}>
            <h3 style={titleStyle}>{title}</h3>
            {isPreview && (
              <p style={previewNoticeStyle}>
                Preview Only - Purchase to access full document
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            style={closeButtonStyle}
            aria-label="Close"
          >
            <CloseIcon />
          </button>
        </header>

        <div style={contentStyle}>
          <PDFViewer
            url={url}
            hideWaterMark={useFullDocument || !doc.hasPassword || !!urlOverride}
            toolbar={false}
          />
          <div style={downloadNoticeStyle}>
            📄 Viewing only - Downloads are not available
          </div>
        </div>
      </div>
    </div>
  );
}

function CloseIcon() {
  return (
    <svg
      style={{ width: '1.5rem', height: '1.5rem' }}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2.5}
        d="M6 18L18 6M6 6l12 12"
      />
    </svg>
  );
}

const backdropStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0,0,0,0.5)',
  zIndex: 999999,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '1rem',
};

const modalStyle = {
  backgroundColor: 'white',
  borderRadius: '16px',
  width: '100%',
  maxWidth: '900px',
  maxHeight: '90vh',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

const headerStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '1rem 1.25rem',
  borderBottom: '1px solid #e5e7eb',
  backgroundColor: DOCUMENTS_PANEL.primaryColor,
};

const headerContentStyle = { flex: 1, minWidth: 0 };

const titleStyle = {
  margin: 0,
  fontSize: '1.125rem',
  fontWeight: '700',
  color: 'white',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const previewNoticeStyle = {
  margin: '0.25rem 0 0 0',
  fontSize: '0.75rem',
  color: 'rgba(255,255,255,0.9)',
};

const closeButtonStyle = {
  padding: '0.5rem',
  backgroundColor: 'transparent',
  border: 'none',
  cursor: 'pointer',
  color: 'white',
  borderRadius: '8px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  marginLeft: '1rem',
};

const contentStyle = {
  flex: 1,
  overflow: 'auto',
  padding: '1rem',
  position: 'relative',
};

const downloadNoticeStyle = {
  position: 'absolute',
  bottom: '1.5rem',
  left: '50%',
  transform: 'translateX(-50%)',
  backgroundColor: 'rgba(0,0,0,0.8)',
  color: 'white',
  padding: '0.5rem 1rem',
  borderRadius: '8px',
  fontSize: '0.875rem',
  pointerEvents: 'none',
};
